import {
  ArrowDown,
  ArrowRight,
  Database,
  Download,
  Upload,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SyncFlowDiagramProps {
  /** ISO timestamp of the last successful order sync */
  orderSyncLastAt?: string | null;
  /** Current product-sync job status */
  productSyncStatus?: string | null; // 'running' | 'completed' | 'failed'
  /** ISO timestamp of the last product sync */
  productSyncLastAt?: string | null;
  /** Whether outbound inventory push is enabled */
  pushEnabled?: boolean;
  /** Whether the last push was healthy */
  pushHealthy?: boolean;
  /** Whether the availability sync row is shown */
  availabilityEnabled?: boolean;
}

// ---------------------------------------------------------------------------
// Health helpers
// ---------------------------------------------------------------------------

const TEN_MINUTES_MS = 10 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function isOrderSyncHealthy(lastAt: string | null | undefined): boolean | undefined {
  if (!lastAt) return undefined;
  const age = Date.now() - new Date(lastAt).getTime();
  return age <= TEN_MINUTES_MS;
}

function isProductSyncHealthy(
  status: string | null | undefined,
  lastAt: string | null | undefined,
): boolean | undefined {
  if (status === 'running') return true;
  if (!lastAt) return undefined;
  const age = Date.now() - new Date(lastAt).getTime();
  return age <= TWENTY_FOUR_HOURS_MS;
}

// ---------------------------------------------------------------------------
// SyncFlowDiagram
// ---------------------------------------------------------------------------

/**
 * Visual diagram showing the two-directional sync flow.
 *
 * Desktop: 5-column grid  —  Inbound | arrow | ERP | arrow | Outbound
 * Mobile:  single column  —  ERP (top) | arrow | Inbound | arrow | Outbound
 *
 * Static with status dots; not interactive (actions belong in the actions section).
 */
export function SyncFlowDiagram({
  orderSyncLastAt,
  productSyncStatus,
  productSyncLastAt,
  pushEnabled,
  pushHealthy,
  availabilityEnabled,
}: SyncFlowDiagramProps) {
  const orderHealthy = isOrderSyncHealthy(orderSyncLastAt);
  const productHealthy = isProductSyncHealthy(productSyncStatus, productSyncLastAt);

  return (
    <div
      role="img"
      aria-label="Sync flow diagram showing inbound data from Shopify into PrintRun and outbound pushes back to Shopify"
      className="rounded-lg border bg-card p-4 sm:p-6"
    >
      {/* ----------------------------------------------------------------- */}
      {/* Desktop layout (md+): 5 columns with arrows between boxes         */}
      {/* ----------------------------------------------------------------- */}
      <div className="hidden md:grid md:grid-cols-[1fr_auto_auto_auto_1fr] md:gap-0 md:items-center">
        {/* Inbound box */}
        <div className="flex flex-col gap-3 rounded-lg border border-dashed border-muted-foreground/25 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Download className="h-4 w-4" />
            Inbound
          </div>
          <div className="space-y-2">
            <SyncRow
              label="Order Sync"
              detail="Every 5 min"
              healthy={orderHealthy}
            />
            <SyncRow
              label="Product Sync"
              detail="Every 4 hrs"
              healthy={productHealthy}
            />
          </div>
        </div>

        {/* Arrow: Inbound -> ERP */}
        <div className="flex items-center justify-center px-3 text-muted-foreground/40">
          <ArrowRight className="h-5 w-5" />
        </div>

        {/* Center — ERP node */}
        <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-primary/30 bg-primary/5 px-6 py-5">
          <Database className="h-5 w-5 text-primary/70" />
          <div className="text-lg font-semibold text-primary">PrintRun</div>
          <div className="text-xs text-muted-foreground text-center">
            Source of Truth
          </div>
        </div>

        {/* Arrow: ERP -> Outbound */}
        <div className="flex items-center justify-center px-3 text-muted-foreground/40">
          <ArrowRight className="h-5 w-5" />
        </div>

        {/* Outbound box */}
        <div className="flex flex-col gap-3 rounded-lg border border-dashed border-muted-foreground/25 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Upload className="h-4 w-4" />
            Outbound
          </div>
          <div className="space-y-2">
            <SyncRow
              label="Inventory Push"
              detail={pushEnabled ? 'Active' : 'Disabled'}
              healthy={pushEnabled ? pushHealthy : undefined}
              disabled={!pushEnabled}
            />
            {availabilityEnabled && (
              <SyncRow
                label="Availability Sync"
                detail="Active"
                healthy={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Mobile layout (<md): stacked — ERP on top, arrows pointing down   */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* ERP node (first on mobile) */}
        <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-primary/30 bg-primary/5 px-6 py-5">
          <Database className="h-5 w-5 text-primary/70" />
          <div className="text-lg font-semibold text-primary">PrintRun</div>
          <div className="text-xs text-muted-foreground text-center">
            Source of Truth
          </div>
        </div>

        {/* Arrow down from ERP */}
        <div className="flex items-center justify-center text-muted-foreground/40">
          <ArrowDown className="h-5 w-5" />
        </div>

        {/* Inbound box */}
        <div className="flex flex-col gap-3 rounded-lg border border-dashed border-muted-foreground/25 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Download className="h-4 w-4" />
            Inbound
          </div>
          <div className="space-y-2">
            <SyncRow
              label="Order Sync"
              detail="Every 5 min"
              healthy={orderHealthy}
            />
            <SyncRow
              label="Product Sync"
              detail="Every 4 hrs"
              healthy={productHealthy}
            />
          </div>
        </div>

        {/* Arrow down to outbound */}
        <div className="flex items-center justify-center text-muted-foreground/40">
          <ArrowDown className="h-5 w-5" />
        </div>

        {/* Outbound box */}
        <div className="flex flex-col gap-3 rounded-lg border border-dashed border-muted-foreground/25 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Upload className="h-4 w-4" />
            Outbound
          </div>
          <div className="space-y-2">
            <SyncRow
              label="Inventory Push"
              detail={pushEnabled ? 'Active' : 'Disabled'}
              healthy={pushEnabled ? pushHealthy : undefined}
              disabled={!pushEnabled}
            />
            {availabilityEnabled && (
              <SyncRow
                label="Availability Sync"
                detail="Active"
                healthy={true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SyncRow — a single row inside an Inbound/Outbound box
// ---------------------------------------------------------------------------

function SyncRow({
  label,
  detail,
  healthy,
  disabled,
}: {
  label: string;
  detail: string;
  healthy?: boolean;
  disabled?: boolean;
}) {
  let dotColor = 'bg-gray-400';
  if (disabled) {
    dotColor = 'bg-gray-400';
  } else if (healthy === true) {
    dotColor = 'bg-green-500';
  } else if (healthy === false) {
    dotColor = 'bg-amber-500';
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${dotColor}`} />
      <span className={`text-sm ${disabled ? 'text-muted-foreground/50' : ''}`}>
        {label}
      </span>
      <span className="text-xs text-muted-foreground ml-auto">{detail}</span>
    </div>
  );
}
